import { getClientAuthToken } from "@/lib/auth/cookies";
import { generateTok } from "@/lib/request-tok";
import { getVisitorId } from "@/lib/fingerprint";

type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

type ClientFetchOptions = {
  method?: HttpMethod;
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
};

type ClientFetchResult<T> =
  | { ok: true; data: T; status: number }
  | { ok: false; data: undefined; status: number; error?: string; errorTitle?: string };

const CLIENT_VERSION = "1.0.0";

function getDeviceType(): string {
  if (typeof window === "undefined") return "unknown";
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "unknown";
  }
}

function getLanguage(): string {
  if (typeof navigator === "undefined") return "unknown";
  return navigator.language || "unknown";
}

function getScreenWidth(): string {
  if (typeof window === "undefined") return "0";
  return String(window.innerWidth);
}

function getScreenHeight(): string {
  if (typeof window === "undefined") return "0";
  return String(window.innerHeight);
}

function getReferrer(): string {
  if (typeof document === "undefined") return "";
  return document.referrer || "";
}

function getPlatform(): string {
  if (typeof navigator === "undefined") return "unknown";
  return navigator.platform || "unknown";
}

// Ensures concurrent 401s share one refresh attempt instead of all hitting /api/auth/refresh
let refreshPromise: Promise<boolean> | null = null

export async function tryRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch("/api/auth/refresh", { method: "POST" })
      .then((r) => r.ok)
      .finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

async function buildHeaders(customHeaders: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Client": "web",
    "X-Client-Version": CLIENT_VERSION,
    "X-Device-Type": getDeviceType(),
    "X-Timezone": getTimezone(),
    "X-Language": getLanguage(),
    "X-Screen-Width": getScreenWidth(),
    "X-Screen-Height": getScreenHeight(),
    "X-Platform": getPlatform(),
    "X-Referrer": getReferrer(),
    ...customHeaders,
  }
  const token = getClientAuthToken()
  if (token) headers["Authorization"] = `Bearer ${token}`
  const tok = await generateTok()
  if (tok) headers["X-Tok"] = tok
  const visitorId = await getVisitorId()
  if (visitorId) headers["X-Visitor-Id"] = visitorId
  return headers
}

/**
 * Client-side fetch utility with automatic Bearer token auth and device headers.
 * Automatically retries once after a silent token refresh on 401 responses.
 */
export async function clientFetch<T = unknown>(
  url: string,
  options: ClientFetchOptions = {},
  isRetry = false
): Promise<ClientFetchResult<T>> {
  const { method = "GET", body, headers: customHeaders = {} } = options;

  const fetchOptions: RequestInit = {
    method,
    headers: await buildHeaders(customHeaders),
  };

  if (body && method !== "GET") {
    fetchOptions.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, fetchOptions);

    // Attempt a silent token refresh on 401, then retry the original request once.
    // Only try if we actually had a token — skip if the user is logged out (no token = no refresh).
    if ((response.status === 401 || response.status === 403) && !isRetry && !url.includes("api/auth")) {
      if (getClientAuthToken()) {
        const refreshed = await tryRefresh()
        if (refreshed) return clientFetch<T>(url, options, true)
      }
    }

    if (response.status >= 400 && response.status < 500) {
      let errorMessage: string | undefined;
      let errorTitle: string | undefined;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error_message || errorData.error || errorData.message;
        errorTitle = errorData.error_title;
      } catch {
        // Response body wasn't JSON
      }
      return { ok: false, data: undefined, status: response.status, error: errorMessage, errorTitle };
    }

    if (!response.ok) {
      return { ok: false, data: undefined, status: response.status };
    }

    if (response.status === 204 || response.headers.get("content-length") === "0") {
      return { ok: true, data: undefined as T, status: response.status };
    }

    const data = await response.json();
    return { ok: true, data, status: response.status };

  } catch (error) {
    return { ok: false, data: undefined, status: 0, error: "Network error" };
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const clientAPI = {
  get: <T = unknown>(url: string, options?: Omit<ClientFetchOptions, "method" | "body">) =>
    clientFetch<T>(url, { ...options, method: "GET" }),

  post: <T = unknown>(url: string, body?: Record<string, unknown>, options?: Omit<ClientFetchOptions, "method" | "body">) =>
    clientFetch<T>(url, { ...options, method: "POST", body }),

  patch: <T = unknown>(url: string, body?: Record<string, unknown>, options?: Omit<ClientFetchOptions, "method" | "body">) =>
    clientFetch<T>(url, { ...options, method: "PATCH", body }),

  put: <T = unknown>(url: string, body?: Record<string, unknown>, options?: Omit<ClientFetchOptions, "method" | "body">) =>
    clientFetch<T>(url, { ...options, method: "PUT", body }),

  delete: <T = unknown>(url: string, options?: Omit<ClientFetchOptions, "method" | "body">) =>
    clientFetch<T>(url, { ...options, method: "DELETE" }),
};
